import React, { useState } from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { Mic, SkipForward, Calendar, RefreshCw } from 'lucide-react';

const PromptContainer = styled.div`
  text-align: center;
  max-width: 500px;
  margin: 0 auto;
`;

const WelcomeBack = styled.h1`
  font-size: 2rem;
  font-weight: 700;
  color: ${({ theme }) => theme.colors.dark.text};
  margin-bottom: ${({ theme }) => theme.spacing.md};
`;

const UserName = styled.span`
  color: ${({ theme }) => theme.colors.primary[400]};
`;

const Description = styled.p`
  font-size: 1.125rem;
  color: ${({ theme }) => theme.colors.dark.textSecondary};
  line-height: 1.6;
  margin-bottom: ${({ theme }) => theme.spacing['2xl']};
`;

const VoiceIcon = styled.div`
  width: 100px;
  height: 100px;
  background: linear-gradient(135deg, 
    ${({ theme }) => theme.colors.primary[500]}20, 
    ${({ theme }) => theme.colors.accent.purple}20
  );
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto ${({ theme }) => theme.spacing.xl};
  
  svg {
    width: 40px;
    height: 40px;
    color: ${({ theme }) => theme.colors.primary[500]};
  }
`;

const LastUpdated = styled.p`
  font-size: 0.875rem;
  color: ${({ theme }) => theme.colors.dark.textSecondary};
  margin-bottom: ${({ theme }) => theme.spacing.xl};
  
  span {
    color: ${({ theme }) => theme.colors.primary[400]};
    font-weight: 500;
  }
`;

const ButtonGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing.md};
  max-width: 350px;
  margin: 0 auto;
`;

const ActionButton = styled(motion.button)`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: ${({ theme }) => theme.spacing.md};
  padding: ${({ theme }) => theme.spacing.md} ${({ theme }) => theme.spacing.xl};
  background: ${({ variant, theme }) => 
    variant === 'primary' 
      ? `linear-gradient(135deg, ${theme.colors.primary[500]}, ${theme.colors.accent.purple})`
      : theme.colors.dark.elevated
  };
  border: ${({ variant, theme }) => 
    variant === 'primary' 
      ? 'none'
      : `1px solid ${theme.colors.dark.border}`
  };
  border-radius: ${({ theme }) => theme.borderRadius.full};
  color: ${({ variant, theme }) => 
    variant === 'primary' 
      ? 'white'
      : theme.colors.dark.text
  };
  font-size: 0.9rem;
  font-weight: 600;
  cursor: pointer;
  transition: all ${({ theme }) => theme.transitions.fast};
  
  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 4px 12px ${({ theme }) => theme.colors.primary[500]}20;
    border-color: ${({ variant, theme }) => 
      variant !== 'primary' && theme.colors.primary[500]
    };
  }
  
  svg {
    width: 18px;
    height: 18px;
  }
`;

const CheckboxOption = styled.label`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.sm};
  margin-top: ${({ theme }) => theme.spacing.lg};
  cursor: pointer;
  font-size: 0.875rem;
  color: ${({ theme }) => theme.colors.dark.textSecondary};
  
  input {
    width: 18px;
    height: 18px;
    accent-color: ${({ theme }) => theme.colors.primary[500]};
  }
  
  &:hover {
    color: ${({ theme }) => theme.colors.dark.text};
  }
`;

const ExistingUserPrompt = ({ onChoice, user }) => {
  const [dontAskAgain, setDontAskAgain] = useState(false);

  const getLastUpdatedText = () => {
    const lastUpdate = localStorage.getItem(`voice_profile_date_${user?.uid || user?.id}`);
    if (lastUpdate) {
      const date = new Date(lastUpdate);
      const days = Math.floor((new Date() - date) / (1000 * 60 * 60 * 24));
      if (days === 0) return 'today';
      if (days === 1) return 'yesterday';
      return `${days} days ago`;
    }
    return 'never';
  };

  const handleSkip = () => {
    if (dontAskAgain) {
      onChoice('skip30');
    } else {
      onChoice('skip');
    }
  };

  return (
    <PromptContainer>
      <VoiceIcon>
        <Mic />
      </VoiceIcon>
      
      <WelcomeBack>
        Welcome back, <UserName>{user?.displayName?.split(' ')[0] || 'User'}</UserName>!
      </WelcomeBack>
      
      <Description>
        Your voice profile helps MyAI recognize you securely. Would you like to update it for better accuracy?
      </Description>
      
      <LastUpdated>
        Last updated: <span>{getLastUpdatedText()}</span>
      </LastUpdated>
      
      <ButtonGroup>
        <ActionButton
          variant="primary"
          onClick={() => onChoice('update')}
          whileHover={{ scale: 1.02 }}
          whileTap={{ scale: 0.98 }}
        >
          <RefreshCw />
          Update Voice Profile
        </ActionButton>
        
        <ActionButton
          variant="secondary"
          onClick={handleSkip}
          whileHover={{ scale: 1.02 }}
          whileTap={{ scale: 0.98 }}
        >
          <SkipForward />
          Skip for Now
        </ActionButton>
      </ButtonGroup>
      
      <CheckboxOption>
        <input
          type="checkbox"
          checked={dontAskAgain}
          onChange={(e) => setDontAskAgain(e.target.checked)}
        />
        <span>Don't ask again for 30 days</span>
      </CheckboxOption>
    </PromptContainer>
  );
};

export default ExistingUserPrompt;
